import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import React from 'react';
import { Customization } from '../../types';
import './SectionReorder.css';

interface SectionReorderProps {
  customization: Customization;
  onCustomizationChange: (customization: Customization) => void;
}

interface SortableItemProps {
  id: string;
  label: string;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, label }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-item"
    >
      <div {...attributes} {...listeners} className="drag-handle">
        <GripVertical size={16} />
      </div>
      <span className="section-label">{label}</span>
    </div>
  );
};

const SectionReorder: React.FC<SectionReorderProps> = ({
  customization,
  onCustomizationChange,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sectionLabels: { [key: string]: string } = {
    personal: 'Personal Information',
    summary: 'Professional Summary',
    experience: 'Work Experience',
    projects: 'Projects',
    education: 'Education',
    skills: 'Skills',
    certifications: 'Certifications',
    awards: 'Awards',
    publications: 'Publications',
    volunteer: 'Volunteer Work',
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = customization.sectionOrder.indexOf(active.id as string);
      const newIndex = customization.sectionOrder.indexOf(over.id as string);

      const newOrder = arrayMove(customization.sectionOrder, oldIndex, newIndex);

      onCustomizationChange({
        ...customization,
        sectionOrder: newOrder,
      });
    }
  };

  if (!isExpanded) {
    return (
      <div className="section-reorder collapsed">
        <button
          onClick={() => setIsExpanded(true)}
          className="expand-btn"
        >
          <GripVertical size={16} />
          <span>Reorder Sections</span>
        </button>
      </div>
    );
  }

  return (
    <div className="section-reorder expanded">
      <div className="reorder-header">
        <div className="flex items-center gap-2">
          <GripVertical size={18} />
          <h3>Reorder Sections</h3>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="collapse-btn"
        >
          Collapse
        </button>
      </div>

      <p className="reorder-hint">
        Drag and drop to reorder how sections appear in your resume
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={customization.sectionOrder.filter(id => id !== 'personal')}
          strategy={verticalListSortingStrategy}
        >
          <div className="sortable-list">
            {customization.sectionOrder
              .filter(id => id !== 'personal')
              .map((sectionId) => (
                <SortableItem
                  key={sectionId}
                  id={sectionId}
                  label={sectionLabels[sectionId] || sectionId}
                />
              ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="reorder-actions">
        <button
          onClick={() => {
            const defaultOrder = ['personal', 'summary', 'experience', 'projects', 'education', 'skills', 'certifications'];
            onCustomizationChange({
              ...customization,
              sectionOrder: defaultOrder,
            });
          }}
          className="btn btn-ghost btn-sm"
        >
          Reset to Default Order
        </button>
      </div>
    </div>
  );
};

export default SectionReorder;

